import { motion } from 'motion/react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBackHandler } from '../../backStack';
import { useBodyScrollLock } from '../useBodyScrollLock';
import { useKeyboardOpen } from '../useKeyboardOpen';

export interface ModalProps {
  /** Click on the backdrop / X-button closes the modal. */
  onClose: () => void;
  /** Modal body. Owns its own card layout, paddings, content. The
   *  Modal frame ONLY provides the backdrop, centring and motion. */
  children: ReactNode;
  /** `data-co-component` written onto the backdrop so a right-click
   *  there opens the right DC section. Defaults to `Modal` (the
   *  shared frame); per-modal consumers can override (e.g.
   *  `inspectName="RevealShippingModal"`) so their content-specific
   *  knobs stay reachable. */
  inspectName?: string;
  /** Optional `aria-labelledby` for the inner dialog. */
  ariaLabelledBy?: string;
  /** Tailwind max-width class for the centred dialog wrapper. Defaults to a
   *  compact `max-w-[420px]`; wider modals (e.g. the item picker grid) opt
   *  into a larger frame so their content fills the wrapper and stays
   *  centred instead of overflowing it. */
  widthClassName?: string;
  /** Close (X) button, or anything else that pins itself to a corner rather
   *  than sitting in the card.
   *
   *  It belongs HERE and not in `children` because the entrance animation
   *  transforms the card, and a transform makes its element the containing
   *  block for `position: fixed` descendants: a close button inside `children`
   *  spends the animation measured from the card's corner (landing on top of
   *  the card content) and then jumps to the viewport corner the moment the
   *  transform is dropped. This slot renders as a sibling of the animated
   *  card, inside the same untransformed positioned wrapper, so `fixed` means
   *  the viewport throughout and `sm:absolute` still resolves against the
   *  card's box. */
  closeSlot?: ReactNode;
}

/**
 * Shared modal frame. Every modal in the app uses this wrapper so a
 * partner can theme the entire modal layer from a single Design
 * Constructor panel — `Modal → backdropFill` / `Modal → backdropBlur`
 * — and the change applies everywhere.
 *
 * The card content (titles, fields, buttons, gradients, borders)
 * is intentionally NOT styled here — it belongs to the consumer in
 * `children`. Per-page uniqueness lives in the children; the frame
 * is uniform.
 *
 * Visual chrome flows through CSS vars:
 *   --co-Modal-backdropFill   tint over the page (alpha colour)
 *   --co-Modal-backdropBlur   gaussian blur on the page behind (px)
 */
export function Modal({
  onClose,
  children,
  inspectName = 'Modal',
  ariaLabelledBy,
  widthClassName = 'max-w-[420px]',
  closeSlot,
}: ModalProps) {
  // Freeze the page while the modal is up — this component is only mounted
  // when open, so the lock's lifetime is the modal's. Must run before the
  // `document === undefined` bail below (hooks can't sit after a return).
  useBodyScrollLock();
  // Host back button closes the modal the same way a backdrop tap does.
  useBackHandler(true, onClose, 'overlay');
  const keyboardOpen = useKeyboardOpen();

  // Render in a portal at document.body so the modal escapes any
  // ancestor that creates a containing block for fixed-positioned
  // descendants (e.g. parents with `backdrop-filter`, `filter`,
  // `transform`, `perspective`, or `will-change` — those clip a
  // `position: fixed` child to the ancestor's bounds instead of the
  // viewport, which would otherwise wedge the modal into the corner
  // of whatever component opened it).
  if (typeof document === 'undefined') return null;

  return createPortal(
    <motion.div
      aria-hidden
      data-co-component={inspectName}
      // `safe-area-pad` on top of `inset-0`: the tint and blur run all the way
      // to the physical edges — dimming the host's status-bar band is what keeps
      // its white glyphs readable, and stopping short of it left a hard seam
      // over the bright page — while the padding keeps the dialog itself clear
      // of the notch and of the host's bottom navigation.
      className="safe-area-pad fixed inset-0 z-50 overflow-y-auto"
      style={{
        // With the keyboard up the bottom edge of the frame is the keyboard,
        // not the host's bottom navigation, so `safe-area-pad`'s bottom inset
        // would only squeeze the dialog for no reason.
        ...(keyboardOpen ? { paddingBottom: 0 } : {}),
        backgroundColor: 'var(--co-Modal-backdropFill, rgba(0,0,0,0.45))',
        backdropFilter: 'blur(var(--co-Modal-backdropBlur, 20px))',
        // Safari still ships `backdrop-filter` behind the `-webkit-`
        // prefix on older versions — duplicate so iOS users get blur too.
        WebkitBackdropFilter: 'blur(var(--co-Modal-backdropBlur, 20px))',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      // Same curve and duration as the dialog below, deliberately: the dialog
      // rides a steep ease-out that lands almost immediately, so a `0.2s
      // easeOut` backdrop arrived visibly AFTER the sheet was already on
      // screen — the dim read as a second, late animation.
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      onClick={onClose}
    >
      {/* Scroll-safe centering. The backdrop is the scroll container; this
          wrapper is at least viewport-tall and centers the dialog, so a modal
          taller than the viewport scrolls from the top instead of clipping.
          Result: every modal is centered on both axes, at any content size.
          `min-h-full` follows the backdrop's box, which the safe area already
          shrank — so the dialog centres inside the safe region.

          With a keyboard up that same `min-h-full` is what let WebKit drag the
          sheet off the top of the screen: it makes this scroll container's
          content taller than the part of it a viewer can see, and WebKit then
          scrolls it to reveal the focused field. Collapsing it to the sheet's
          own height leaves nothing to scroll, and top-aligning puts the fields
          in the visible strip instead of behind the keyboard. */}
      <div
        className="flex min-h-full items-center justify-center p-4"
        {...(keyboardOpen ? { style: { minHeight: 0, alignItems: 'flex-start' } } : {})}
      >
        {/* Untransformed positioning context: it carries `relative` for the
            `closeSlot`'s desktop `sm:absolute`, and stopping the click here
            (rather than on the animated card) keeps a tap on a corner button
            from also reaching the backdrop and closing twice. */}
        <div
          className={'relative w-full ' + widthClassName}
          onClick={(e) => e.stopPropagation()}
        >
          {closeSlot}
          <motion.div
            role="dialog"
            aria-modal="true"
            {...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {})}
            initial={{ y: 16, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 16, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </motion.div>,
    document.body,
  );
}
